<template>
  <LoadingCard :loading="loading" class="overflow-visible">
    <!-- Header -->
    <div class="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center gap-3 min-w-0">
        <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
          <svg class="h-4.5 w-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 9v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div class="min-w-0">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">گزارش مالی آموزشگاه‌های زیرمجموعه</h3>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
            فروش پرداخت‌شده‌ی دوره‌ها و پکیج‌های آموزشگاه‌های عضو کانون شما
          </p>
        </div>
      </div>

      <button
        @click="fetch()"
        :disabled="loading"
        class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        title="بارگذاری مجدد"
      >
        <svg :class="['h-4 w-4', loading ? 'animate-spin' : '']" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </button>
    </div>

    <!-- Filters -->
    <div class="px-4 sm:px-6 py-3 border-b border-gray-100 dark:border-gray-700">
      <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <!--
          Not a <label>: the picker renders its popup inside this wrapper, so a
          label would forward every click on the popup back to the input and
          toggle the picker shut the moment it opened.
        -->
        <div class="block">
          <span class="mb-1 block text-xs text-gray-500 dark:text-gray-400">از تاریخ</span>
          <date-picker
            v-model="filters.from"
            type="date"
            format="jYYYY/jMM/jDD"
            display-format="jYYYY/jMM/jDD"
            :clearable="true"
            :input-attrs="{ style: 'direction: ltr; text-align: right;' }"
            :input-class="fieldClass"
            placeholder="ابتدای بازه"
          />
        </div>

        <div class="block">
          <span class="mb-1 block text-xs text-gray-500 dark:text-gray-400">تا تاریخ</span>
          <date-picker
            v-model="filters.to"
            type="date"
            format="jYYYY/jMM/jDD"
            display-format="jYYYY/jMM/jDD"
            :clearable="true"
            :input-attrs="{ style: 'direction: ltr; text-align: right;' }"
            :input-class="fieldClass"
            placeholder="انتهای بازه"
          />
        </div>

        <label class="block">
          <span class="mb-1 block text-xs text-gray-500 dark:text-gray-400">آموزشگاه</span>
          <select v-model="filters.institute_id" :class="fieldClass">
            <option :value="null">همه آموزشگاه‌ها</option>
            <option v-for="institute in filterOptions.institutes" :key="institute.id" :value="institute.id">
              {{ institute.name }}
            </option>
          </select>
        </label>

        <label class="block">
          <span class="mb-1 block text-xs text-gray-500 dark:text-gray-400">نوع محصول</span>
          <select v-model="filters.product_type" :class="fieldClass">
            <option :value="null">دوره و پکیج</option>
            <option value="course">دوره</option>
            <option value="package">پکیج</option>
          </select>
        </label>

        <label class="block">
          <span class="mb-1 block text-xs text-gray-500 dark:text-gray-400">جستجوی عنوان</span>
          <input
            v-model="filters.search"
            type="text"
            placeholder="عنوان دوره یا پکیج..."
            :class="[fieldClass, 'placeholder-gray-400']"
          />
        </label>
      </div>

      <div class="mt-3 flex flex-wrap items-center gap-2">
        <button
          v-for="preset in presets"
          :key="preset.key"
          type="button"
          @click="applyPreset(preset)"
          class="inline-flex h-7 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-2.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          {{ preset.label }}
        </button>

        <button
          v-if="hasFilters"
          type="button"
          @click="resetFilters"
          class="inline-flex h-7 items-center rounded-lg px-2.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
        >
          حذف فیلترها
        </button>
      </div>
    </div>

    <!-- Summary -->
    <div class="grid gap-3 px-4 sm:px-6 py-4 sm:grid-cols-2 lg:grid-cols-4">
      <div class="rounded-xl border border-gray-100 dark:border-gray-700 px-4 py-3">
        <p class="text-xs text-gray-400">مجموع فروش</p>
        <p class="mt-1 text-sm font-bold text-gray-900 dark:text-gray-100">{{ money(summary.sales_total) }}</p>
      </div>
      <div class="rounded-xl border border-gray-100 dark:border-gray-700 px-4 py-3">
        <p class="text-xs text-gray-400">تعداد سفارش</p>
        <p class="mt-1 text-sm font-bold text-gray-900 dark:text-gray-100">{{ toFa(summary.orders_count) }}</p>
      </div>
      <div class="rounded-xl border border-gray-100 dark:border-gray-700 px-4 py-3">
        <p class="text-xs text-gray-400">تعداد فروش (آیتم)</p>
        <p class="mt-1 text-sm font-bold text-gray-900 dark:text-gray-100">{{ toFa(summary.items_count) }}</p>
      </div>
      <div class="rounded-xl border border-gray-100 dark:border-gray-700 px-4 py-3">
        <p class="text-xs text-gray-400">آموزشگاه‌های فروشنده</p>
        <p class="mt-1 text-sm font-bold text-gray-900 dark:text-gray-100">{{ toFa(summary.institutes_count) }}</p>
      </div>
    </div>

    <!-- Per institute breakdown -->
    <div v-if="byInstitute.length > 0" class="px-4 sm:px-6 pb-4">
      <p class="mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">تفکیک بر اساس آموزشگاه</p>
      <div class="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
              <th class="px-4 py-2.5 text-start font-medium">آموزشگاه</th>
              <th class="px-4 py-2.5 text-start font-medium">مجموع فروش</th>
              <th class="px-4 py-2.5 text-center font-medium">سفارش</th>
              <th class="px-4 py-2.5 text-center font-medium">آیتم</th>
              <th class="px-4 py-2.5 text-center font-medium">سهم</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700/60">
            <tr
              v-for="row in byInstitute"
              :key="row.institute_id"
              class="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
              @click="filterByInstitute(row.institute_id)"
            >
              <td class="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">{{ row.institute_name }}</td>
              <td class="px-4 py-2.5 text-gray-700 dark:text-gray-300">{{ money(row.sales_total) }}</td>
              <td class="px-4 py-2.5 text-center text-gray-600 dark:text-gray-300">{{ toFa(row.orders_count) }}</td>
              <td class="px-4 py-2.5 text-center text-gray-600 dark:text-gray-300">{{ toFa(row.items_count) }}</td>
              <td class="px-4 py-2.5 text-center text-xs text-gray-500">{{ share(row.sales_total) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty -->
    <div v-if="!loading && rows.length === 0" class="flex flex-col items-center justify-center py-12 px-6 text-center">
      <p class="text-sm font-medium text-gray-600 dark:text-gray-300">
        {{ hasFilters ? 'برای این فیلترها فروشی ثبت نشده است' : 'هنوز فروشی برای آموزشگاه‌های زیرمجموعه ثبت نشده است' }}
      </p>
      <p class="mt-1 text-xs text-gray-400">فقط سفارش‌های پرداخت‌شده در این گزارش می‌آیند.</p>
    </div>

    <!-- Sale lines -->
    <div v-else class="overflow-x-auto border-t border-gray-100 dark:border-gray-700">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
            <th class="px-4 sm:px-6 py-2.5 text-start font-medium">تاریخ پرداخت</th>
            <th class="px-4 py-2.5 text-start font-medium">سفارش</th>
            <th class="px-4 py-2.5 text-start font-medium">آموزشگاه</th>
            <th class="px-4 py-2.5 text-start font-medium">محصول</th>
            <th class="px-4 py-2.5 text-center font-medium">تعداد</th>
            <th class="px-4 py-2.5 text-start font-medium">مبلغ</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-gray-700/60">
          <tr v-for="row in rows" :key="row.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
            <td class="px-4 sm:px-6 py-3 text-xs text-gray-500">
              <span dir="ltr">{{ jalali(row.paid_at) }}</span>
            </td>
            <td class="px-4 py-3 text-xs text-gray-500">
              <span dir="ltr">{{ row.order_number || '—' }}</span>
            </td>
            <td class="px-4 py-3 text-gray-700 dark:text-gray-300">{{ row.institute_name }}</td>
            <td class="px-4 py-3">
              <div class="flex items-center gap-2 min-w-0">
                <span
                  :class="[
                    'inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium shrink-0',
                    row.product_type === 'package'
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400'
                      : 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400',
                  ]"
                >
                  {{ row.product_type === 'package' ? 'پکیج' : 'دوره' }}
                </span>
                <span class="truncate text-gray-900 dark:text-gray-100">{{ row.product_title }}</span>
              </div>
            </td>
            <td class="px-4 py-3 text-center text-gray-600 dark:text-gray-300">{{ toFa(row.quantity) }}</td>
            <td class="px-4 py-3 font-semibold text-gray-900 dark:text-gray-100">{{ money(row.line_total) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div
      v-if="meta.total > 0"
      class="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3 border-t border-gray-100 dark:border-gray-700"
    >
      <p class="text-xs text-gray-500 dark:text-gray-400">
        نمایش {{ toFa(meta.from) }} تا {{ toFa(meta.to) }} از {{ toFa(meta.total) }} ردیف فروش
      </p>

      <div v-if="meta.last_page > 1" class="flex items-center gap-1">
        <button
          @click="goTo(meta.current_page - 1)"
          :disabled="meta.current_page <= 1 || loading"
          class="inline-flex h-8 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-3 text-xs text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          قبلی
        </button>

        <button
          v-for="page in pageWindow"
          :key="page"
          @click="goTo(page)"
          :disabled="loading"
          :class="[
            'inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg px-2 text-xs font-medium transition-colors',
            page === meta.current_page
              ? 'bg-primary-500 text-white'
              : 'border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50',
          ]"
        >
          {{ toFa(page) }}
        </button>

        <button
          @click="goTo(meta.current_page + 1)"
          :disabled="meta.current_page >= meta.last_page || loading"
          class="inline-flex h-8 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-3 text-xs text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          بعدی
        </button>
      </div>
    </div>
  </LoadingCard>
</template>

<script>
import Vue3PersianDatetimePicker from 'vue3-persian-datetime-picker'
import jMoment from 'moment-jalaali'

export default {
  name: 'KanoonFinanceCard',

  components: {
    DatePicker: Vue3PersianDatetimePicker,
  },

  props: {
    card: { type: Object, required: true },
  },

  data: () => ({
    loading: true,
    rows: [],
    byInstitute: [],
    summary: { sales_total: 0, orders_count: 0, items_count: 0, institutes_count: 0, products_count: 0 },
    filterOptions: { institutes: [] },
    meta: { current_page: 1, last_page: 1, total: 0, from: 0, to: 0 },
    filters: { from: null, to: null, institute_id: null, product_type: null, search: '' },
    filterTimer: null,
    presets: [
      { key: 'this-month', label: 'این ماه', unit: 'jMonth' },
      { key: 'last-3-months', label: 'سه ماه اخیر', months: 3 },
      { key: 'this-year', label: 'امسال', unit: 'jYear' },
    ],
  }),

  computed: {
    endpoint() {
      return this.card?.apiEndpoint ?? '/dashboard-api/kanoon/finance'
    },

    // A select renders taller than an input for the same padding, so every
    // filter control shares one class with an explicit height instead.
    fieldClass() {
      return 'h-10 w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 text-sm text-gray-800 dark:text-gray-200 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400'
    },

    hasFilters() {
      const { from, to, institute_id, product_type, search } = this.filters
      return Boolean(from || to || institute_id || product_type || search)
    },

    // Keep the pager to a sliding window so a long report stays readable
    pageWindow() {
      const last = this.meta.last_page
      const current = this.meta.current_page
      const start = Math.max(1, Math.min(current - 2, last - 4))
      const end = Math.min(last, start + 4)
      const pages = []
      for (let page = start; page <= end; page++) pages.push(page)
      return pages
    },
  },

  watch: {
    // One debounced watcher, so typing in the search box and picking a date
    // both settle before a request goes out
    filters: {
      deep: true,
      handler() {
        clearTimeout(this.filterTimer)
        this.filterTimer = setTimeout(() => this.fetch(1), 400)
      },
    },
  },

  created() {
    this.fetch()
  },

  beforeUnmount() {
    clearTimeout(this.filterTimer)
  },

  methods: {
    toFa(value) {
      if (value == null) return '۰'
      return Number(value).toLocaleString('fa-IR')
    },

    money(value) {
      return `${this.toFa(value ?? 0)} تومان`
    },

    share(amount) {
      const total = this.summary.sales_total
      if (!total) return '—'
      return `${this.toFa(Math.round((amount / total) * 100))}٪`
    },

    jalali(value) {
      if (!value) return '—'
      return jMoment(value, 'YYYY-MM-DD HH:mm:ss').format('jYYYY/jMM/jDD')
    },

    // The picker holds Jalali text; the API only ever sees Gregorian dates
    toGregorian(value) {
      if (!value) return undefined
      return jMoment(value, 'jYYYY/jMM/jDD').format('YYYY-MM-DD')
    },

    applyPreset(preset) {
      const now = jMoment()
      const start = preset.months
        ? now.clone().subtract(preset.months, 'jMonth').startOf('jMonth')
        : now.clone().startOf(preset.unit)

      this.filters.from = start.format('jYYYY/jMM/jDD')
      this.filters.to = now.format('jYYYY/jMM/jDD')
    },

    filterByInstitute(instituteId) {
      this.filters.institute_id = this.filters.institute_id === instituteId ? null : instituteId
    },

    resetFilters() {
      this.filters = { from: null, to: null, institute_id: null, product_type: null, search: '' }
    },

    goTo(page) {
      if (page < 1 || page > this.meta.last_page) return
      this.fetch(page)
    },

    async fetch(page = null) {
      this.loading = true
      try {
        const { data } = await Nova.request().get(this.endpoint, {
          params: {
            page: page ?? this.meta.current_page,
            from: this.toGregorian(this.filters.from),
            to: this.toGregorian(this.filters.to),
            institute_id: this.filters.institute_id || undefined,
            product_type: this.filters.product_type || undefined,
            search: this.filters.search || undefined,
          },
        })

        this.rows = data.rows ?? []
        this.byInstitute = data.by_institute ?? []
        this.summary = data.summary ?? this.summary
        this.filterOptions = data.filter_options ?? this.filterOptions
        this.meta = data.meta ?? this.meta
      } catch (error) {
        this.rows = []
        Nova.error(error?.response?.data?.message ?? 'دریافت گزارش مالی با خطا مواجه شد.')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
